import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Card, 
  CardActionArea, 
  CardContent, 
  Button,
  useTheme,
  useMediaQuery,
  Avatar
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import BuildIcon from '@mui/icons-material/Build';
import DescriptionIcon from '@mui/icons-material/Description';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningIcon from '@mui/icons-material/Warning';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TranslateIcon from '@mui/icons-material/Translate';
import TimerIcon from '@mui/icons-material/Timer';

import WinterJobReport from './WinterJobReport';
import SummerJobReport from './SummerJobReport';
import VehicleInspectionReport from './VehicleInspectionReport';
import ContractServicesReport from './ContractServicesReport';
import TimeTrackingReport from './TimeTrackingReport';
import EmployeeDisciplineReport from './EmployeeDisciplineReport';
import LiveLogsReport from './LiveLogsReport';
import SiteTimeReport from './SiteTimeReport';
import QuoteReport from './QuoteReport';
import TruckProfitMarginReport from './TruckProfitMarginReport';
import TranslationsReport from './TranslationsReport';

const ReportsMain = ({ initialReportId = null }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedReportId, setSelectedReportId] = useState(initialReportId);

  const reports = [
    {
      id: 'winter-jobs',
      label: t('reports.winterJobs.title'),
      description: t('reports.winterJobs.description', 'View winter job logs and activities'),
      icon: <AcUnitIcon fontSize="large" />,
      color: '#2196f3',
      component: <WinterJobReport />
    },
    {
      id: 'summer-jobs',
      label: t('reports.summerJobs.title'),
      description: t('reports.summerJobs.description', 'View summer landscaping logs'),
      icon: <WbSunnyIcon fontSize="large" />,
      color: '#ff9800',
      component: <SummerJobReport />
    },
    {
      id: 'vehicle-inspections',
      label: t('reports.vehicleInspections.title'),
      description: t('reports.vehicleInspections.description', 'Track vehicle inspection records'),
      icon: <BuildIcon fontSize="large" />,
      color: '#607d8b',
      component: <VehicleInspectionReport />
    },
    {
      id: 'contract-services',
      label: t('reports.contractServices.title'),
      description: t('reports.contractServices.description', 'Analyze contract service performance'),
      icon: <DescriptionIcon fontSize="large" />,
      color: '#4caf50',
      component: <ContractServicesReport />
    },
    {
      id: 'time-tracking',
      label: t('reports.timeTracking.title'),
      description: t('reports.timeTracking.description', 'Employee hours and time logs'),
      icon: <AccessTimeIcon fontSize="large" />,
      color: '#9c27b0',
      component: <TimeTrackingReport />
    },
    {
      id: 'employee-discipline',
      label: t('reports.discipline.title'),
      description: t('reports.discipline.description', 'Warnings, points, and infractions'),
      icon: <WarningIcon fontSize="large" />,
      color: '#f44336',
      component: <EmployeeDisciplineReport />
    },
    {
      id: 'live-logs',
      label: t('reports.liveLogs.title'),
      description: t('reports.liveLogs.description', 'GPS geofence activity logs'),
      icon: <LocationOnIcon fontSize="large" />,
      color: '#009688',
      component: <LiveLogsReport />
    },
    {
      id: 'site-time',
      label: t('reports.siteTime.title'),
      description: t('reports.siteTime.description'),
      icon: <TimerIcon fontSize="large" />,
      color: '#00796b',
      component: <SiteTimeReport />
    },
    {
      id: 'quotes',
      label: t('reports.quotesReport.title'),
      description: t('reports.quotesReport.description', 'Quote history and status'),
      icon: <RequestQuoteIcon fontSize="large" />,
      color: '#795548',
      component: <QuoteReport />
    },
    {
      id: 'truck-profit-margin',
      label: t('reports.truckProfitMargin.title'),
      description: t('reports.truckProfitMargin.description', 'Revenue vs costs by truck'),
      icon: <TrendingUpIcon fontSize="large" />,
      color: '#1976d2',
      component: <TruckProfitMarginReport />
    },
    {
      id: 'translations-audit',
      label: t('reports.translationsAudit.title', 'Translations Audit'),
      description: t(
        'reports.translationsAudit.shortDescription',
        'EN + ES-MX side-by-side for translator review'
      ),
      icon: <TranslateIcon fontSize="large" />,
      color: '#f57c00',
      component: <TranslationsReport />
    }
  ];
  const selectedReport = reports.find((report) => report.id === selectedReportId) || null;

  const handleReportClick = (report) => {
    setSelectedReportId(report.id);
  };

  const handleBack = () => {
    setSelectedReportId(null);
  };

  // If a report is selected, show the report view
  if (selectedReport) {
    return (
      <Box>
        <Box display="flex" alignItems="center" mb={3}>
          <Button 
            startIcon={<ArrowBackIcon />} 
            onClick={handleBack}
            variant="outlined"
            sx={{ mr: 2 }}
          >
            {t('common.back', 'Back')}
          </Button>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: selectedReport.color }}>
            {selectedReport.label}
          </Typography>
        </Box>
        {selectedReport.component}
      </Box>
    );
  }

  // Otherwise show the dashboard of cards
  return (
    <Box data-demo-target="page-reports-root">
      <Box mb={4}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#f57c00', textAlign: 'left', mb: 1 }}>
          {t('pages.reportsAndAnalytics')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Select a report category to view details and generate PDFs.
        </Typography>
      </Box>
      
      <Grid container spacing={3} data-demo-target="page-reports-grid">
        {reports.map((report) => (
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={report.id}>
            <Card 
              sx={{ 
                height: '100%', 
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4
                }
              }}
            >
              <CardActionArea 
                onClick={() => handleReportClick(report)}
                aria-label={t('reports.openReport', { report: report.label })}
                sx={{ height: '100%', p: 2, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'flex-start' }}
              >
                <Avatar 
                  sx={{ 
                    bgcolor: `${report.color}20`, 
                    color: report.color, 
                    width: 56, 
                    height: 56, 
                    mb: 2 
                  }}
                >
                  {report.icon}
                </Avatar>
                <CardContent sx={{ p: 0, width: '100%' }}>
                  <Typography variant="h6" gutterBottom component="div" fontWeight="bold">
                    {report.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {report.description}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default ReportsMain;
